import type { LocalizedCopy } from '@/@types/preferences';

/**
 * Merges localized custom copy with default treatment options.
 * Only applies custom copy if it exists and is not empty (empty = use frontend default).
 *
 * @param defaultTreatments - The default treatment options
 * @param customCopy - The custom localized copy from preferences
 * @param lang - The language key ('fr' or 'en')
 * @returns The merged treatment options with custom descriptions where applicable
 */
export function mergeLocalizedCustomCopy<
  T extends Record<string, { description: string }>,
>(
  defaultTreatments: T,
  customCopy: Partial<Record<keyof T, LocalizedCopy>> | undefined,
  lang: 'fr' | 'en',
): T {
  if (!customCopy) {
    return defaultTreatments;
  }

  return {
    ...defaultTreatments,
    ...Object.keys(customCopy).reduce((acc, key) => {
      const treatmentKey = key as keyof typeof defaultTreatments;
      const customLangValue = customCopy[treatmentKey]?.[lang];
      // Only use custom copy if it exists and is not empty (empty = use frontend default)
      if (
        customLangValue &&
        customLangValue !== '' &&
        defaultTreatments[treatmentKey]
      ) {
        acc[treatmentKey] = {
          ...defaultTreatments[treatmentKey],
          description: customLangValue,
        };
      }
      return acc;
    }, {} as T),
  };
}
